// Mirror of agent/foyr_agent/ingest/mapper.py::_REQUIRED_FIELDS.
// Single source of truth for what an onboarding wizard must collect
// to spin up a new project.

export interface RequiredField {
  label: string;
  ontology_iri: string;
  why: string;
  format: "short_text" | "single_choice" | "multi_choice" | "number" | "date";
  choices?: readonly string[];
}

// Stable IRI constants — consumers should never reference fields by
// label string. Look them up via `IRI.*` and the renderer reads the
// resolved IngestField by `ontology_iri`.
export const IRI = {
  projectKind:  "bot:Building",
  city:         "schema:addressLocality",
  carpetArea:   "qudt:Area",
  bedrooms:     "bot:Zone",
  budgetTier:   "gr:Offering",
  designStyle:  "schema:style",
  moveInDate:   "time:Instant",
  occupants:    "foaf:Person",
} as const;

export const REQUIRED_FIELDS: readonly RequiredField[] = [
  {
    label: "Project kind",
    ontology_iri: "bot:Building",
    why: "Determines which room rules and BOQ vendors apply.",
    format: "single_choice",
    choices: ["residential", "commercial", "retail", "hospitality"],
  },
  {
    label: "Project city",
    ontology_iri: "schema:addressLocality",
    why: "Pulls local procurement vendors and climate-aware rule packs.",
    format: "short_text",
  },
  {
    label: "Carpet area (sqft)",
    ontology_iri: "qudt:Area",
    why: "Drives the BOQ baseline and lighting/HVAC sizing rules.",
    format: "number",
  },
  {
    label: "Number of bedrooms",
    ontology_iri: "bot:Zone",
    why: "Each bedroom gets its own rule sub-pack (privacy, lighting, materials).",
    format: "number",
  },
  {
    label: "Budget tier",
    ontology_iri: "gr:Offering",
    why: "Steers the substitute-suggester between standard/premium/luxury SKUs.",
    format: "single_choice",
    choices: ["standard", "premium", "luxury"],
  },
  {
    label: "Design style",
    ontology_iri: "schema:style",
    why: "Controls palette + finish library and the designer-voice fingerprint.",
    format: "single_choice",
    choices: ["modern", "scandi", "indian-traditional", "japandi", "industrial"],
  },
  {
    label: "Move-in date",
    ontology_iri: "time:Instant",
    why: "Aligns approval thresholds, handover countdown, and procurement alerts.",
    format: "date",
  },
  {
    label: "Primary occupants",
    ontology_iri: "foaf:Person",
    why: "Personalises the client copilot voice and the rule pack (kids, elders, pets).",
    format: "multi_choice",
    choices: ["couple", "kids", "elders", "pets", "home-office"],
  },
];
